import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function Success() {
  const { t } = useLang();
  const { clearCart } = useCart();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [orderId, setOrderId] = useState(null);
  const navigate = useNavigate();
  const hasPolled = useRef(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("failed"); return; }
    if (hasPolled.current) return;
    hasPolled.current = true;

    let attempts = 0;
    const maxAttempts = 8;
    const interval = 2000;

    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setOrderId(data.order_id);
          clearCart();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        attempts++;
        if (attempts >= maxAttempts) { setStatus("failed"); return; }
        setTimeout(poll, interval);
      } catch {
        setStatus("failed");
      }
    };
    poll();
  }, [params, clearCart]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center jh-card p-10" data-testid="payment-result">
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-[#E05D3A] animate-spin" />
            <h1 className="font-display text-2xl font-black text-[#1A1A1A] mt-6">{t("paymentChecking")}</h1>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#00A85A]/10 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-12 h-12 text-[#00A85A]" />
            </div>
            <h1 className="font-display text-3xl font-black text-[#1A1A1A] mt-6">{t("paymentSuccess")}</h1>
            <div className="mt-8 flex flex-col gap-3">
              {orderId && (
                <Button
                  onClick={() => navigate(`/order/${orderId}`)}
                  className="rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white py-6"
                  data-testid="success-track-btn"
                >
                  {t("trackOrder")}
                </Button>
              )}
              <Button
                onClick={() => navigate("/restaurants")}
                variant="outline"
                className="rounded-full border-[#EAEAEA] py-6"
              >
                {t("continueShopping")}
              </Button>
            </div>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <XCircle className="w-16 h-16 mx-auto text-red-500" />
            <h1 className="font-display text-2xl font-black text-[#1A1A1A] mt-6">{t("paymentFailed")}</h1>
            <Button
              onClick={() => navigate("/cart")}
              className="mt-8 rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white py-6"
            >
              {t("backToMenu")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
